import { Component, inject, input, OnInit, output, signal } from '@angular/core';
import { PermissionsService } from '../../services/permissions/permissions.service';
import { environment } from '../../../environments/environment';
import { UserListResponse } from '../../interfaces/admin.interface';
import { Data, PermissionsAddUpdateRequest, PermissionsResponse, UserPermissionsResponse } from '../../services/permissions/permissions.interface';
import { NotificationComponent } from '../../paginas/pagos/pagos-online/helpers/notification.component';
import { AdminService } from '../../services/admin.service';

@Component({
  selector: 'app-modal-permisos',
  imports: [NotificationComponent],
  templateUrl: './modal-permisos.component.html',
  styleUrl: './modal-permisos.component.css',
})
export class ModalPermisosComponent implements OnInit {
  mostrarModal = input<boolean>(false);
  userSelected = input<UserListResponse | null>(null);
  confirmar = output<void>();
  cancelar = output<void>();
  private permissionsService = inject(PermissionsService);
  private adminService = inject(AdminService);
  permissionsList = this.permissionsService.permissionsList;
  userPermissionsList = this.permissionsService.userPermissionsList;
  selectedPermissions = signal<string[]>([]);
  showNotification = false;
  notificationType: 'success' | 'error' | 'warning' = 'error';
  notificationTitle = '';
  notificationMessage = '';

  ngOnInit(): void {
    this.getPermissions();
    this.getUserPermissions();
  }

 

  /**
   * Actualiza la selección de un permiso según el estado del checkbox.
   */
  onPermisoChange(id: string, event: Event): void {
    const selected = (event.target as HTMLInputElement).checked;

    this.selectedPermissions.update((permissions) =>
      selected
        ? [...permissions, id]
        : permissions.filter((permissionId) => permissionId !== id)
    );
  }

  /**
   * Confirma la selección de permisos y los guarda directamente en el backend.
   */
  onConfirm(): void {
    const userExternalId = this.userSelected()?.externalId;

    if (!userExternalId) {
      return;
    }

    const selectedPermissionIds = this.selectedPermissions();
    const updatedPermissions = this.permissionsList().filter((permission) =>
      selectedPermissionIds.includes(permission.externalId)
    );

    const menuPermissionIds = this.adminService
      .menuPermissionsList()
      .map((permission) => permission.externalId);

    const permissionExternalIds = [
      ...new Set([
        ...updatedPermissions.map((permission) => permission.externalId),
        ...menuPermissionIds,
      ]),
    ];

    const request: PermissionsAddUpdateRequest = {
      UserExternalId: userExternalId,
      ClientId: environment.client_id,
      Permissions: permissionExternalIds.map((externalId) => ({
        ExternalId: externalId,
      })),
    };

    this.permissionsService.addUpdateUserPermissions(request).subscribe({
      next: () => {
        this.userPermissionsList.set(updatedPermissions);
        this.permissionsService.syncInitialUserPermissions();
        this.mostrarNotificacion(
          'success',
          'Permisos actualizados',
          'Los permisos del usuario se guardaron correctamente'
        );
        this.confirmar.emit();
      },
      error: (error) => {
        console.error('Error al guardar permisos del usuario:', error);
        this.mostrarNotificacion(
          'error',
          'Error',
          error?.error?.messages?.[0]?.description ||
            'Error al guardar los permisos del usuario'
        );
      },
    });
  }


  /**
   * Obtiene el catálogo de permisos disponibles.
   */
  getPermissions(): void {
    this.permissionsService
      .getPermissions({ ClientId: environment.client_id })
      .subscribe({
        next: (response: PermissionsResponse) => {
          const permissions = response.data.filter((permission: Data) => !permission.isMenu);
          this.permissionsList.set(permissions);
        },
        error: (error) => {
          console.error('Error al cargar el catálogo de permisos:', error);
          this.mostrarNotificacion(
            'error',
            'Error',
            error?.error?.messages?.[0]?.description ||
              'Error al cargar el catálogo de permisos'
          );
        },
      });
  }

  /**
   * Obtiene los permisos asignados al usuario y marca los checks.
   */
  getUserPermissions(): void {
    const userExternalId = this.userSelected()?.externalId;

    if (!userExternalId) {
      return;
    }

    const currentUserPermissions = this.userPermissionsList();

    if (currentUserPermissions.length > 0) {
      this.selectedPermissions.set(
        currentUserPermissions.map((permission) => permission.externalId)
      );
      return;
    }

    this.selectedPermissions.set([]);

    this.permissionsService
      .getUserPermissions({
        UserExternalId: userExternalId,
        ClientId: environment.client_id,
      })
      .subscribe({
        next: (response: UserPermissionsResponse) => {
          const userPermissions = response.data.permissions.filter((permission: Data) => !permission.isMenu);
          this.selectedPermissions.set(
            userPermissions.map((permission: Data) => permission.externalId)
          );
        },
        error: (error) => {
          console.error('Error al cargar permisos del usuario:', error);
          this.mostrarNotificacion(
            'error',
            'Error',
            error?.error?.messages?.[0]?.description ||
              'Error al cargar los permisos del usuario'
          );
        },
      });
  }

  /**
   * Muestra una notificación temporal al usuario.
   */
  mostrarNotificacion(
    type: 'success' | 'error' | 'warning',
    title: string,
    message: string
  ): void {
    this.notificationType = type;
    this.notificationTitle = title;
    this.notificationMessage = message;
    this.showNotification = true;

    setTimeout(() => {
      this.showNotification = false;
    }, 5000);
  }

  /**
   * Cancela la selección de permisos.
   */
  onCancel(): void {
    this.cancelar.emit();
  }
}
